import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { ModelPicker, ModelPickerValue } from '@/components/ModelPicker';
import { RichTextEditor } from '@/components/RichTextEditor';
import { documentsService, CrmDocument, Letterhead, DocumentType, DocumentLineItem } from '@/services/documents.service';
import { Save, Sparkles, Send, Loader2, ArrowLeft, FileText, Download, BookmarkPlus, CheckCircle2, FilePlus2, Undo2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCompanyCurrency, useCurrencyField } from '@/contexts/WorkspaceContext';

const TYPE_LABELS: Record<DocumentType, string> = {
  quotation: 'Quotation', invoice: 'Invoice', proposal: 'Proposal',
  note: 'Note', contract: 'Contract', other: 'Other',
};

export default function DocumentEditor() {
  const companyCurrency = useCompanyCurrency();
  const { uid } = useParams<{ uid: string }>();
  const nav = useNavigate();
  const [doc, setDoc] = useState<CrmDocument | null>(null);
  const [letterhead, setLetterhead] = useState<Letterhead | null>(null);
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // Form state
  const [type, setType] = useState<DocumentType>('quotation');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useCurrencyField();
  const [validUntil, setValidUntil] = useState('');
  const [lineItems, setLineItems] = useState<DocumentLineItem[]>([]);
  const [defaultTaxRate, setDefaultTaxRate] = useState<number | undefined>(undefined);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendChannel, setSendChannel] = useState<'whatsapp' | 'email'>('whatsapp');
  const [sendTo, setSendTo] = useState('');
  const [sendPrefix, setSendPrefix] = useState('');
  const [sendBusy, setSendBusy] = useState(false);

  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiModel, setAiModel] = useState<ModelPickerValue | undefined>(undefined);

  useEffect(() => {
    if (!uid) return;
    Promise.all([
      documentsService.get(uid),
      documentsService.letterhead().catch(() => null),
    ]).then(([d, l]) => {
      setDoc(d);
      setLetterhead(l);
      setType(d.type);
      setTitle(d.title);
      setBody(d.body);
      setAmount(d.amount != null ? String(d.amount) : '');
      setCurrency(d.currency ?? companyCurrency);
      setValidUntil(d.validUntil ? d.validUntil.slice(0, 10) : '');
      setLineItems(d.lineItems ?? []);
      setDefaultTaxRate(d.taxRatePct);
    }).catch(() => toast.error('Failed to load document'));
  }, [uid]);

  const save = async () => {
    if (!uid) return;
    setBusy(true);
    try {
      const updated = await documentsService.update(uid, {
        title,
        body,
        amount: amount ? parseFloat(amount) : undefined,
        currency,
        validUntil: validUntil || undefined,
        lineItems: lineItems.length ? lineItems : undefined,
      });
      setDoc(updated);
      // Sync header amount from server if line items recomputed it.
      if (updated.amount != null) setAmount(String(updated.amount));
      if (updated.lineItems) setLineItems(updated.lineItems);
      toast.success('Saved');
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const markSent = async () => {
    if (!uid) return;
    setBusy(true);
    try {
      const updated = await documentsService.markSent(uid);
      setDoc(updated);
      toast.success('Marked as sent');
    } finally { setBusy(false); }
  };

  const doSend = async () => {
    if (!uid || !sendTo.trim()) { toast.error('Recipient required'); return; }
    setSendBusy(true);
    try {
      if (sendChannel === 'whatsapp') {
        const r = await documentsService.sendWhatsapp(uid, { phone: sendTo.trim(), messagePrefix: sendPrefix.trim() || undefined });
        if (r.ok) {
          toast.success('Sent via WhatsApp');
          setSendOpen(false);
          // Refresh doc to pick up sentAt + status changes.
          const d = await documentsService.get(uid);
          setDoc(d);
        } else {
          toast.error(`Send failed: ${r.error ?? 'unknown error'}`);
        }
      } else {
        const r = await documentsService.sendEmailPrep(uid, { email: sendTo.trim(), bodyPrefix: sendPrefix.trim() || undefined });
        // Open the user's mail client with prefilled subject + body.
        window.location.href = r.mailto;
        toast.success('Email opened in your mail client');
        setSendOpen(false);
        const d = await documentsService.get(uid);
        setDoc(d);
      }
    } catch { /* interceptor */ } finally { setSendBusy(false); }
  };

  const runAi = async () => {
    if (!uid || !aiPrompt.trim()) return;
    setAiBusy(true);
    try {
      const r = await documentsService.aiAssist(uid, {
        prompt: aiPrompt.trim(),
        provider: aiModel?.provider,
        model: aiModel?.model,
        context: [],
      });
      setBody(r.text);
      toast.success(`AI generated (${r.tokensIn + r.tokensOut} tokens)`);
      setAiPrompt('');
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setAiBusy(false); }
  };

  const previewParagraphs = useMemo(() => body.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean), [body]);

  if (!doc) {
    return (
      <>
        <Topbar title="Document" breadcrumb="Documents" />
        <div className="mx-auto w-full max-w-[1200px] px-7 py-7">
          <Card><div className="skeleton h-80 rounded-lg" /></Card>
        </div>
      </>
    );
  }

  const downloadHtml = () => {
    if (!uid) return;
    // Open the printable HTML in a new tab. The page has a "Download PDF" button
    // that triggers the browser's native print-to-PDF.
    window.open(documentsService.renderHtmlUrl(uid), '_blank', 'noopener,noreferrer');
  };

  const saveAsTemplate = async () => {
    if (!uid) return;
    setBusy(true);
    try {
      // Save current edits first so the template captures them, then flag.
      await documentsService.update(uid, { title, body, amount: amount ? parseFloat(amount) : undefined, currency, validUntil: validUntil || undefined });
      const tpl = await documentsService.markAsTemplate(uid, true);
      setDoc(tpl);
      toast.success(`"${tpl.title}" saved as a reusable template`);
    } catch { /* interceptor */ } finally { setBusy(false); }
  };

  // ── Accounting-lite: payment + convert actions ────────────────────────────
  const markPaid = async () => {
    if (!uid) return;
    setBusy(true);
    try {
      const updated = await documentsService.markPaid(uid, {
        paidAmount: amount ? parseFloat(amount) : undefined,
        paymentMethod: 'manual',
      });
      setDoc(updated);
      toast.success('Marked paid');
    } catch { /* interceptor */ } finally { setBusy(false); }
  };

  const markUnpaid = async () => {
    if (!uid) return;
    setBusy(true);
    try {
      const updated = await documentsService.markUnpaid(uid);
      setDoc(updated);
      toast.success('Marked unpaid');
    } catch { /* interceptor */ } finally { setBusy(false); }
  };

  const convertToInvoice = async () => {
    if (!uid) return;
    setBusy(true);
    try {
      const inv = await documentsService.convertToInvoice(uid);
      toast.success(`Created invoice "${inv.title}"`);
      nav(`/documents/${inv.documentUid}`);
    } catch { /* interceptor */ } finally { setBusy(false); }
  };

  return (
    <>
      <Topbar
        title={title || 'Untitled'}
        breadcrumb={`Documents / ${TYPE_LABELS[type]}`}
        back={
          <button
            onClick={() => nav('/documents')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors"
            aria-label="Back to documents"
          >
            <ArrowLeft size={16} />
          </button>
        }
        right={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setAiOpen((v) => !v)} iconLeft={<Sparkles size={14} />}>
              {aiOpen ? 'Close AI' : 'AI assist'}
            </Button>
            <Button variant="secondary" onClick={saveAsTemplate} loading={busy} iconLeft={<BookmarkPlus size={14} />}>
              {doc.isTemplate ? 'Re-save as template' : 'Save as template'}
            </Button>
            <Button variant="secondary" onClick={downloadHtml} iconLeft={<Download size={14} />}>Download</Button>
            {doc.type === 'quotation' && doc.status !== 'declined' && (
              <Button variant="secondary" onClick={convertToInvoice} loading={busy} iconLeft={<FilePlus2 size={14} />}>
                Convert to invoice
              </Button>
            )}
            {doc.type === 'invoice' && !doc.paidAt && (
              <Button variant="secondary" onClick={markPaid} loading={busy} iconLeft={<CheckCircle2 size={14} />}>
                Mark paid
              </Button>
            )}
            {doc.type === 'invoice' && doc.paidAt && (
              <Button variant="ghost" onClick={markUnpaid} loading={busy} iconLeft={<Undo2 size={14} />}>
                Unpaid
              </Button>
            )}
            <Button variant="secondary" onClick={() => setSendOpen(true)} loading={busy} iconLeft={<Send size={14} />}>
              Send
            </Button>
            <Button variant="primary" onClick={save} loading={busy} iconLeft={<Save size={14} />}>Save</Button>
          </div>
        }
      />

      <div className="mx-auto grid w-full max-w-[1400px] gap-5 px-7 py-7 lg:grid-cols-[1fr_420px]">
        {/* ── Editor ───────────────────────────────────────────────── */}
        <div className="space-y-5">
          <Card>
            <SectionHeader title="Document details" icon={<FileText size={16} />} />
            <FormField label="Title" required>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Quotation for Acme Corp" />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Type">
                <Select value={type} onChange={(e) => setType(e.target.value as DocumentType)} disabled>
                  {(Object.keys(TYPE_LABELS) as DocumentType[]).map((t) => (
                    <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Status">
                <Input value={doc.status} disabled />
              </FormField>
              <FormField label="Amount">
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              </FormField>
              <FormField label="Currency">
                <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={4} />
              </FormField>
              <FormField label="Valid until">
                <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
              </FormField>
            </div>
          </Card>

          {(type === 'quotation' || type === 'invoice' || type === 'proposal') && uid && (
            <Card>
              <LineItemsEditor
                documentUid={uid}
                value={lineItems}
                onChange={setLineItems}
                currency={currency}
                defaultTaxRatePct={defaultTaxRate}
              />
            </Card>
          )}

          <div>
            <div className="mb-2 flex items-center justify-between">
              <SectionHeader title="Body" subtitle="Word-style editor — paragraphs, headings, lists, tables, links, images. AI Assist drafts or rewrites on demand." />
            </div>
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder="Start writing — your blank page is ready."
            />
          </div>
        </div>

        {/* ── Right column: AI panel + letterhead preview ────────── */}
        <div className="space-y-5">
          {aiOpen && (
            <Card className="border-primary/40 bg-primary-soft/30">
              <SectionHeader title="AI assist" icon={<Sparkles size={16} />} subtitle="Drafts use your business profile + tone." />
              <FormField label="What should the AI write or revise?">
                <Textarea
                  rows={3}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Draft a quotation for 50 hours of mobile dev at $80/hr, payment in 2 milestones."
                />
              </FormField>
              <FormField label="Model" hint="Defaults to your company default">
                <ModelPicker value={aiModel} onChange={setAiModel} size="sm" />
              </FormField>
              <Button
                variant="primary"
                onClick={runAi}
                loading={aiBusy}
                disabled={!aiPrompt.trim() || aiBusy}
                className="w-full"
              >
                {aiBusy ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate body</>}
              </Button>
              <p className="mt-2 text-2xs text-text-muted">
                Replaces the current body. Save when you're happy with the output.
              </p>
            </Card>
          )}

          <Card padded={false} className="overflow-hidden">
            <div className="border-b border-border bg-surface-alt/40 px-4 py-2 text-2xs uppercase tracking-wide text-text-muted">
              Letterhead preview
            </div>
            <div className="space-y-4 px-6 py-5 text-sm">
              {/* Header */}
              <div className="flex items-start justify-between border-b border-border pb-3">
                <div>
                  {letterhead?.logoUrl
                    ? <img src={letterhead.logoUrl} alt="" className="mb-2 h-10 w-auto" />
                    : <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-gold-gradient text-on-primary"><FileText size={18} /></div>}
                  <div className="font-display text-base font-bold text-text-primary">{letterhead?.companyName || 'Your Company'}</div>
                  {letterhead?.tagline && <div className="text-xs text-text-muted">{letterhead.tagline}</div>}
                </div>
                <div className="text-right text-xs text-text-muted">
                  <div>{TYPE_LABELS[type]}</div>
                  <div className="mt-0.5">{new Date().toLocaleDateString()}</div>
                </div>
              </div>

              {/* Body preview */}
              <div className="space-y-3">
                <div className="font-display text-base font-semibold text-text-primary">{title || 'Untitled'}</div>
                {previewParagraphs.length === 0 && (
                  <div className="text-xs italic text-text-muted">Body preview appears here as you type…</div>
                )}
                {previewParagraphs.slice(0, 6).map((p, i) => (
                  <p key={i} className="text-xs leading-relaxed text-text-secondary whitespace-pre-line">{p}</p>
                ))}
                {previewParagraphs.length > 6 && (
                  <p className="text-2xs italic text-text-muted">… +{previewParagraphs.length - 6} more paragraph{previewParagraphs.length - 6 === 1 ? '' : 's'}</p>
                )}
              </div>

              {/* Footer */}
              {amount && (
                <div className="border-t border-border pt-3 text-right">
                  <div className="text-2xs uppercase tracking-wide text-text-muted">Total</div>
                  <div className="font-display text-lg font-bold text-text-primary">{parseFloat(amount).toLocaleString()} {currency}</div>
                  {validUntil && <div className="mt-0.5 text-2xs text-text-muted">Valid until {new Date(validUntil).toLocaleDateString()}</div>}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Modal open={sendOpen} onClose={() => !sendBusy && setSendOpen(false)} title={`Send ${doc?.type ?? 'document'}`} widthClass="max-w-md">
        <div className="px-6 py-5 space-y-3">
          <div className="inline-flex rounded-lg border border-border bg-surface-alt p-0.5">
            {(['whatsapp', 'email'] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setSendChannel(c)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  sendChannel === c ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary'
                }`}
              >
                {c === 'whatsapp' ? 'WhatsApp' : 'Email'}
              </button>
            ))}
          </div>
          <FormField label={sendChannel === 'whatsapp' ? 'Recipient phone' : 'Recipient email'} required>
            <Input
              value={sendTo}
              onChange={(e) => setSendTo(e.target.value)}
              placeholder={sendChannel === 'whatsapp' ? '+15551234567' : 'name@example.com'}
              type={sendChannel === 'whatsapp' ? 'tel' : 'email'}
            />
          </FormField>
          <FormField label="Message" hint="Optional intro line. We'll add the document link automatically.">
            <Input
              value={sendPrefix}
              onChange={(e) => setSendPrefix(e.target.value)}
              placeholder={sendChannel === 'whatsapp' ? `Hi — your ${doc?.type ?? 'document'} is ready.` : 'Hi, please review the attached.'}
            />
          </FormField>
          <p className="text-2xs text-text-muted">
            The document is shared via a private public-view link. The recipient sees it without needing an account.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" onClick={() => setSendOpen(false)} disabled={sendBusy}>Cancel</Button>
          <Button variant="primary" onClick={doSend} disabled={sendBusy || !sendTo.trim()} loading={sendBusy} iconLeft={<Send size={14} />}>
            {sendChannel === 'whatsapp' ? 'Send via WhatsApp' : 'Open email'}
          </Button>
        </div>
      </Modal>
    </>
  );
}

interface LineItemsEditorProps {
  documentUid: string;
  value: DocumentLineItem[];
  onChange: (next: DocumentLineItem[]) => void;
  currency: string;
  defaultTaxRatePct?: number;
}

function LineItemsEditor({ documentUid, value, onChange, currency, defaultTaxRatePct }: LineItemsEditorProps) {
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiAppend, setAiAppend] = useState(false);

  const runAi = async () => {
    if (!aiBrief.trim()) { toast.error('Brief required'); return; }
    setAiBusy(true);
    try {
      const r = await documentsService.aiLineItems(documentUid, { brief: aiBrief.trim() });
      const generated: DocumentLineItem[] = r.lineItems.map((li, i) => ({
        id: `li_${Date.now()}_${i}`,
        description: li.description,
        quantity: li.quantity,
        unitPrice: li.unitPrice,
        taxRatePct: li.taxRatePct,
      }));
      onChange(aiAppend ? [...value, ...generated] : generated);
      toast.success(`Generated ${generated.length} line item${generated.length === 1 ? '' : 's'}`);
      setAiOpen(false);
      setAiBrief('');
    } catch { /* interceptor */ } finally { setAiBusy(false); }
  };

  const addRow = () => {
    onChange([
      ...value,
      { id: `li_${Math.random().toString(36).slice(2, 10)}`, description: '', quantity: 1, unitPrice: 0 },
    ]);
  };

  const updateRow = (idx: number, patch: Partial<DocumentLineItem>) => {
    onChange(value.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const removeRow = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const subtotal = value.reduce((s, r) => s + (r.quantity || 0) * (r.unitPrice || 0), 0);
  const taxTotal = value.reduce((s, r) => {
    const sub = (r.quantity || 0) * (r.unitPrice || 0);
    const rate = r.taxRatePct ?? defaultTaxRatePct ?? 0;
    return s + sub * (rate / 100);
  }, 0);
  const grandTotal = subtotal + taxTotal;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <SectionHeader title="Line items" subtitle="Itemise the quote — totals on the document update from this table." className="!mb-0" />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setAiAppend(value.length > 0); setAiOpen(true); }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-info/30 bg-info-soft px-3 py-1.5 text-xs font-semibold text-info hover:brightness-110"
            title="Generate line items from a brief"
          >
            <Sparkles size={12} /> Generate with AI
          </button>
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary-soft px-3 py-1.5 text-xs font-semibold text-primary hover:brightness-110"
          >
            + Add row
          </button>
        </div>
      </div>
      {value.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-alt/30 px-4 py-6 text-center text-xs text-text-muted">
          No line items. Click "Add row" to start, or skip and use a single header amount.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-2xs uppercase tracking-[0.14em] text-text-muted">
                <th className="px-2 py-2 text-left">Description</th>
                <th className="px-2 py-2 text-right w-20">Qty</th>
                <th className="px-2 py-2 text-right w-32">Unit price</th>
                <th className="px-2 py-2 text-right w-20">Tax %</th>
                <th className="px-2 py-2 text-right w-32">Line total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {value.map((row, i) => {
                const sub = (row.quantity || 0) * (row.unitPrice || 0);
                const rate = row.taxRatePct ?? defaultTaxRatePct ?? 0;
                const lineTotal = sub * (1 + rate / 100);
                return (
                  <tr key={row.id} className="border-t border-border align-top">
                    <td className="px-2 py-2">
                      <input
                        type="text"
                        value={row.description}
                        onChange={(e) => updateRow(i, { description: e.target.value })}
                        placeholder="What you're charging for…"
                        className="w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-sm focus:outline-none focus:border-primary"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={row.quantity}
                        onChange={(e) => updateRow(i, { quantity: Number(e.target.value || 0) })}
                        className="w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-right text-sm focus:outline-none focus:border-primary"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min={0}
                        step="any"
                        value={row.unitPrice}
                        onChange={(e) => updateRow(i, { unitPrice: Number(e.target.value || 0) })}
                        className="w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-right text-sm focus:outline-none focus:border-primary"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step="any"
                        value={row.taxRatePct ?? ''}
                        onChange={(e) => updateRow(i, { taxRatePct: e.target.value === '' ? undefined : Number(e.target.value) })}
                        placeholder={defaultTaxRatePct != null ? String(defaultTaxRatePct) : '0'}
                        className="w-full rounded-lg border border-border bg-surface px-2 py-1.5 text-right text-sm focus:outline-none focus:border-primary"
                      />
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-sm font-semibold text-text-primary">
                      {lineTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-1 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        className="rounded-md p-1 text-text-muted hover:bg-danger-soft hover:text-danger"
                        title="Remove row"
                      >×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border text-sm">
                <td colSpan={4} className="px-2 py-2 text-right text-text-muted">Subtotal</td>
                <td className="px-2 py-2 text-right tabular-nums">{subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} {currency}</td>
                <td />
              </tr>
              <tr>
                <td colSpan={4} className="px-2 py-1 text-right text-text-muted">Tax</td>
                <td className="px-2 py-1 text-right tabular-nums">{taxTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} {currency}</td>
                <td />
              </tr>
              <tr className="border-t border-border">
                <td colSpan={4} className="px-2 py-2 text-right font-semibold text-text-primary">Total</td>
                <td className="px-2 py-2 text-right tabular-nums font-display text-base font-bold text-text-primary">
                  {grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })} {currency}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <Modal open={aiOpen} onClose={() => !aiBusy && setAiOpen(false)} title="Generate line items with AI" widthClass="max-w-lg">
        <div className="px-6 py-5 space-y-3">
          <FormField label="Brief" required hint="Describe what's being quoted. AI will draft the line items — you'll review before saving.">
            <Textarea
              rows={4}
              value={aiBrief}
              onChange={(e) => setAiBrief(e.target.value)}
              placeholder="Brand identity package: logo design (3 concepts, 2 rounds revisions), brand guidelines doc, business card design, 90-day support."
            />
          </FormField>
          {value.length > 0 && (
            <label className="flex items-center gap-2 text-xs text-text-secondary">
              <input
                type="checkbox"
                checked={aiAppend}
                onChange={(e) => setAiAppend(e.target.checked)}
                className="accent-primary"
              />
              Append to existing rows (uncheck to replace)
            </label>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" onClick={() => setAiOpen(false)} disabled={aiBusy}>Cancel</Button>
          <Button variant="primary" onClick={runAi} disabled={aiBusy || !aiBrief.trim()} loading={aiBusy} iconLeft={<Sparkles size={14} />}>
            Generate
          </Button>
        </div>
      </Modal>
    </div>
  );
}
